'use client';

import { useEffect } from 'react';
import { usePathname, useRouter } from 'next/navigation';

import { useAuthUser } from '@/hooks/use-auth-user';
import { cleanSharedFiles } from '@/lib/download';
import { appRedirectFor, isNativeApp, pathFromAppLink } from '@/lib/native';

/** The link the app was launched with is remembered, so it is not reopened on every load. */
const HANDLED_LAUNCH_LINK = 'nl:launch-link';

/**
 * The Android back button.
 *
 * The shell only walks back through history and does nothing when there is nowhere left to go,
 * which left the user stuck in the app. Here the button first closes an open dialog, the way
 * Escape does in the browser, otherwise goes one step back, and sends the app to the background
 * on the first page.
 */
export function goBack(canGoBack: boolean, minimize: () => void): void {
  const dialog = document.querySelector<HTMLElement>('[role="dialog"][data-state="open"]');
  if (dialog) {
    // Radix dialogs close on Escape, the same as the browser does.
    dialog.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
    return;
  }
  if (canGoBack) window.history.back();
  else minimize();
}

/**
 * What the App Store / Google Play app needs beyond the website: opening on the login and
 * keeping to the pages behind it, accepting links that open the app, and a back button that
 * behaves like other apps. Renders nothing, and does nothing in a browser.
 */
export function NativeApp() {
  const pathname = usePathname();
  const router = useRouter();
  const user = useAuthUser();

  // The app shows only the pages behind the login; see appRedirectFor.
  useEffect(() => {
    if (!isNativeApp() || user === undefined || !pathname) return;
    const target = appRedirectFor(pathname, user !== null);
    if (target) router.replace(target);
  }, [pathname, user, router]);

  useEffect(() => {
    if (!isNativeApp()) return;
    let cancelled = false;
    const removers: (() => void)[] = [];

    const open = (link: string) => {
      const path = pathFromAppLink(link);
      if (!path) return;
      try {
        window.sessionStorage.setItem(HANDLED_LAUNCH_LINK, link);
      } catch {
        // Without storage we only risk one extra navigation.
      }
      // A full load, not a router navigation, so the page starts from a clean state.
      window.location.assign(path);
    };

    async function listen() {
      const { App } = await import('@capacitor/app');
      const listeners = await Promise.all([
        App.addListener('appUrlOpen', ({ url }) => open(url)),
        // Only Android has the button; on iPhone the event never fires.
        App.addListener('backButton', ({ canGoBack }) => goBack(canGoBack, () => void App.minimizeApp())),
      ]);
      if (cancelled) {
        for (const l of listeners) void l.remove();
        return;
      }
      removers.push(...listeners.map((l) => () => void l.remove()));

      // When the app was closed when the link was tapped, it does not arrive as an event: the
      // shell loads the front page and the link has to be fetched from here.
      const launch = await App.getLaunchUrl().catch(() => undefined);
      if (!launch?.url) return;
      let handled: string | null = null;
      try {
        handled = window.sessionStorage.getItem(HANDLED_LAUNCH_LINK);
      } catch {
        // Treated as new.
      }
      if (handled === null) open(launch.url);
    }

    void listen();
    void cleanSharedFiles();
    return () => {
      cancelled = true;
      for (const remove of removers) remove();
    };
  }, []);

  return null;
}
